import { toast } from '@/components/ToastProvider';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { createOrder } from '@/lib/api';
import { createPaymentIntent } from '@/services/payment';
import { useBranchStore } from '@/store/branchStore';
import { useCartStore } from '@/store/cartStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { CartSummary } from './CartSummary';
import { DeliveryForm, DeliveryFormValues } from './DeliveryForm';

const formSchema = z
  .object({
    deliveryOption: z.enum(['pickup', 'delivery'], {
      required_error: 'Please select a delivery option',
    }),
    firstName: z.string().min(2, 'First name is required'),
    lastName: z.string().min(2, 'Last name is required'),
    email: z.string().email('Invalid email address'),
    phone: z.string().min(10, 'Phone number is required'),
    address: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.deliveryOption === 'delivery') {
      if (!data.address || data.address.length < 5) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Address is required for delivery',
          path: ['address'],
        });
      }
      if (!data.city || data.city.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'City is required for delivery',
          path: ['city'],
        });
      }
      if (!data.state || data.state.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'State is required for delivery',
          path: ['state'],
        });
      }
      if (!data.postalCode || data.postalCode.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Postal code is required for delivery',
          path: ['postalCode'],
        });
      }
    }
  });

export function CheckoutForm() {
  const items = useCartStore(s => s.items);
  const clearCart = useCartStore(s => s.clearCart);
  const closeCart = useCartStore(s => s.closeCart);
  const isPrescriptionRequired = useCartStore(s => s.isPrescriptionRequired);

  const branchId = useBranchStore(s => s.branchId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<DeliveryFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onTouched',
    defaultValues: {
      deliveryOption: 'pickup',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
    },
  });

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = form.getValues();

      if (!branchId) {
        throw new Error('Branch ID not found');
      }

      const orderItems = items.map(item => ({
        id: item.id,
        name: item.brand_name,
        Product_Name: item.brand_name,
        quantity: item.quantity,
        price: item.price,
        Selling_Price: item.price,
      }));

      const dialCode = formData.phone.startsWith('+')
        ? formData.phone.split(' ')[0]
        : '+234';

      const phoneNumber = formData.phone.startsWith('+')
        ? formData.phone.split(' ').slice(1).join('')
        : formData.phone;

      const orderRequest = {
        branchId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        address: JSON.stringify({
          address: formData.address || 'N/A',
          location: {},
        }),
        recipient_firstname: formData.firstName,
        recipient_lastname: formData.lastName,
        recipient_email: formData.email,
        recipient_phone: phoneNumber,
        city: formData.city || 'N/A',
        state: formData.state || 'N/A',
        items: orderItems,
        callbackUrl: `${window.location.origin}/order-success`,
        recipient: 'Yourself',
        dialCode,
        phoneNumber,
        coordinates: [],
        channel: 'storefront',
        delivery_fee: 0,
        user_first_name: formData.firstName,
        user_last_name: formData.lastName,
        user_email: formData.email || '',
        user_phone_number: phoneNumber || '',
        deliveryOption: formData.deliveryOption,
        providerId: import.meta.env.VITE_PROVIDER_ID,
        source: 'manual',
      };

      const response = await createOrder(orderRequest);

      if (!response?.success || !response?.order) {
        toast.error('Order creation failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      const orderData = response.order;
      const orderMeta = orderData.meta;

      if (orderData.payment_link) {
        window.location.href = orderData.payment_link;
        return;
      }

      const paymentIntent = await createPaymentIntent({
        amount: Math.round(Number(orderData.total || 0)),
        payment_option: [
          'bank_transfer',
          'card',
          'cash',
          'ussd',
          'mobile_money',
          'qr',
          'pay_for_me',
          'insurance',
          'lifeline',
          'split_payment',
        ],
        callback_url: `${window.location.origin}/order-success`,
        email: orderMeta?.recipient_email || orderMeta?.user_email || '',
        phone: orderMeta?.recipient_phone || orderMeta?.user_phone_number || '',
        first_name: orderMeta?.user_first_name ?? '',
        last_name: orderMeta?.user_last_name ?? '',
        orderId: orderData.db_formatted_id,
      });

      const intentData = paymentIntent?.data?.intent?.data;

      if (typeof intentData === 'string' && intentData.startsWith('https')) {
        window.location.href = intentData;
      } else if (intentData && typeof intentData === 'object') {
        const intentId = intentData.id;
        window.location.href = `${
          import.meta.env.VITE_PAYMENT_BASE_URL
        }?id=${intentId}`;
      } else {
        throw new Error('Invalid payment intent response');
      }

      toast.success('Order created!  Redirecting to payment...');
      clearCart();
      closeCart();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to create order. Please try again.'
      );
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={handleFormSubmit} className="space-y-3 md:space-y-4 px-2">
        <Accordion
          type="single"
          collapsible
          defaultValue="item-1"
          className="w-full"
        >
          <AccordionItem value="item-1">
            <AccordionTrigger className="text-sm md:text-base border-b-0 border-none px-0">
              Delivery Details
            </AccordionTrigger>
            <AccordionContent className="px-0">
              <DeliveryForm
                form={form}
                requiresPrescription={isPrescriptionRequired()}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-2">
            <AccordionTrigger
              className="text-sm md:text-base border-b-0 border-none px-0"
              style={{ textDecoration: 'none' }}
            >
              Order Summary
            </AccordionTrigger>
            <AccordionContent className="px-0">
              <CartSummary deliveryOption={form.watch('deliveryOption')} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Button
          type="submit"
          className="w-full h-10 text-sm"
          disabled={
            isSubmitting || !form.formState.isValid || items.length === 0
          }
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating Order...
            </>
          ) : (
            'Proceed to Payment'
          )}
        </Button>
      </form>
    </Form>
  );
}
